import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import gql from 'graphql-tag';
import { IonicModule } from '@ionic/angular';
import { map, shareReplay, switchMap, startWith, first } from 'rxjs/operators';
import * as Sentry from "@sentry/angular";
import { ApplyPipeModule, CopyToClipboardModule, TitleContentComponent, ToastFacade, UiPromiseService } from 'ngx-zcs-components';
import { isNotNullOrUndefined, MigrateSunflowerPlantsMutation } from 'ngx-core-services';

import { AccountMigrationService } from './account-migration.service';

export const getLabels = () => ({
  complete: "End Migration",
  confirm: "Confirm",
  description: "Still have systems on the old ZCS portal? Enter your account credentials to import them to the new platform in just a few steps. If a previous migration attempt failed, you can complete it again to retry.",
  email: "Email from the old portal",
  emailInvalid: "Email is invalid",
  emailRequired: "Email is required",
  errorTryAgain: "Error during migration, please try again",
  manageMigration: "Manage Migration",
  manageMigrationTitle: "The migration request has been accepted, you can now import your systems.",
  migrationCompletedSuccess: "Migration completed successfully",
  migrationStatus: "Migration Status",
  onProgress: "On Progress",
  pending: "Pending",
  error: "Error",
  password: "Password from the old portal",
  passwordRequired: "Password is required",
  pushToCompleteDescription: "Confirm to end the migration from this account.",
  pushToCompleteTitle: "Once you’ve imported all the systems you want to, you can end the process.",
  requestMigration: "Request Migration",
  requestMigrationSuccess: "Migration request sent successfully",
  unexpectedError: "Unexpected error",
});

@Component({
  selector: 'lib-account-migration',
  standalone: true,
  imports: [
    ApplyPipeModule,
    CommonModule,
    CopyToClipboardModule,
    FormsModule,
    IonicModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TitleContentComponent,
  ],
  templateUrl: './account-migration.component.html',
  styleUrls: ['./account-migration.component.scss'],
})
export class AccountMigrationComponent implements OnInit, OnDestroy {
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): ReturnType<typeof getLabels> {
    return this._labels;
  }

  @Output() readonly manageMigration = new EventEmitter<void>();

  pollingTimer?: Subscription;
  readonly refresh$ = new Subject<void>();

  readonly migrationDetails$ = this.accountMigrationService.user$.pipe(
    isNotNullOrUndefined(),
    switchMap(user =>
      this.refresh$.pipe(
        startWith(undefined),
        switchMap(() => this.accountMigrationService.accountMigrationDetails(user.uid)),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isMigrationAvailable$ = this.migrationDetails$.pipe(
    map((details) => details?.migration.status === 'completed' || details === null),
  );

  readonly toLabel = (text?: "pending" | "migrated" | "error" | "completed") => {
    switch (text) {
      case "pending":
        return this.labels.pending;
      case "migrated":
        return this.labels.onProgress;
      case "error":
        return this.labels.error;
      default:
        return text?.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    }
  };

  constructor(
    private readonly accountMigrationService: AccountMigrationService,
    private readonly migrateSunflowerPlantsMutation: MigrateSunflowerPlantsMutation,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
  ) {
    this.migrationRequest = this.migrationRequest.bind(this);
    this.completeMigration = this.completeMigration.bind(this);
  }

  ngOnInit(): void {
    this.pollingTimer = timer(0, 1000 * 30).subscribe(() => this.refresh$.next());
  }

  ngOnDestroy(): void {
    this.pollingTimer?.unsubscribe();
  }

  async migrationRequest(form: NgForm) {
    if (form.invalid) {
      return;
    }

    const query = gql`
    fragment migratesunflowerresponse on MigrateSunflowerResponse {
      message
      success
    }`;

    const pResponse = await this.uiPromiseService.run(() => this.migrateSunflowerPlantsMutation.execute(query, { input: {
      email: form.value.emailSunflower,
      password: form.value.passwordSunflower,
    }}), {
      loading: true,
    });

    if (!pResponse?.success?.data?.migrateSunflowerPlants?.success) {
      await this.toastFacade.present(this.labels.errorTryAgain, this.labels.unexpectedError, { severity: 'error' });
    } else {
      await this.toastFacade.present('', this.labels.requestMigrationSuccess, { severity: 'success' });

      const userId = await this.accountMigrationService.user$.pipe(
        isNotNullOrUndefined(),
        map(user => user.uid),
        first(),
      ).toPromise();

      Sentry.captureEvent({
        message: `Migration request initiated for user ${userId}`,
        level: "info",
        tags: {
          operation: "account-migration-request",
          userId,
        },
        extra: {
          email: form.value.emailSunflower,
          timestamp: Math.floor(Date.now() / 1000),
        },
      });
    }

    this.refresh$.next();
  }

  async completeMigration(docId?: string) {
    if (!docId) {
      return;
    }

    const userId = await this.accountMigrationService.user$.pipe(
      isNotNullOrUndefined(),
      map(user => user.uid),
      first(),
    ).toPromise();

    const pResponse = await this.uiPromiseService.run(() => this.accountMigrationService.completeLatestMigration(userId, docId), {
      loading: true,
    });

    if (!pResponse) {
      await this.toastFacade.present(this.labels.errorTryAgain, this.labels.unexpectedError, { severity: 'error' });
    } else {
      await this.toastFacade.present('', this.labels.migrationCompletedSuccess, { severity: 'success' });

      Sentry.captureEvent({
        message: `Migration completed for user ${userId}`,
        level: "info",
        tags: {
          operation: "account-migration-complete",
          userId,
          docId,
        },
        extra: {
          timestamp: Math.floor(Date.now() / 1000),
        },
      });
    }

    this.refresh$.next();
  }
}
